import React, { useEffect, useState } from "react";
import { getClients, updateClient, getClientsCount, deleteClient } from "./api";
import "./ListClients.css";
import {Pencil ,UserMinus,Eye} from "@phosphor-icons/react";

function ListClients() {
  const [clients, setClients] = useState([]);
  const [clientsCount, setClientsCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClients, setSelectedClients] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showMenu, setShowMenu] = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log("Loading clients data...");
        const [clientsData, countData] = await Promise.all([
          getClients(token),
          getClientsCount(token)
        ]);
        console.log("Clients data:", clientsData); // Debug
        console.log("Clients data type:", typeof clientsData);
        console.log("Clients data is array:", Array.isArray(clientsData));
        console.log("Count data:", countData);
        
        // Ensure clientsData is an array
        if (Array.isArray(clientsData)) {
          setClients(clientsData);
        } else if (clientsData && clientsData.message) {
          // If it's an error response, set empty array
          console.error("API Error:", clientsData.message);
          setClients([]);
        } else {
          // Fallback to empty array
          console.warn("Unexpected clients data format:", clientsData);
          setClients([]);
        }
        
        // Ensure countData has the expected structure
        if (countData && typeof countData.count === 'number') {
          setClientsCount(countData.count);
        } else {
          setClientsCount(0);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setClients([]);
        setClientsCount(0);
      }
    };
    loadData();
  }, [token]);

  // Filtrer les clients selon la recherche
  const filteredClients = Array.isArray(clients) ? clients.filter(client =>
    client.contact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.nom_entreprise?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.telephone?.includes(searchTerm)
  ) : [];

  // Gestion de la sélection
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedClients(filteredClients.map(client => client.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (clientId, checked) => {
    if (checked) {
      setSelectedClients([...selectedClients, clientId]);
    } else {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    }
  };

  // Gestion du menu d'actions
  const handleMenuClick = (clientId, e) => {
    e.stopPropagation();
    setShowMenu(showMenu === clientId ? null : clientId);
  };

  // Fermer le menu si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = () => setShowMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Voir les détails d'un client
  const handleViewClient = (client) => {
    setCurrentClient(client);
    setShowViewModal(true);
    setShowMenu(null);
  };

  // Modifier un client
  const handleEditClient = (client) => {
    setCurrentClient(client);
    setEditForm({ ...client });
    setShowEditModal(true);
    setShowMenu(null);
  };

  // Sauvegarder les modifications
  const handleSaveEdit = async () => {
    try {
      await updateClient(token, currentClient.id, editForm);
      setShowEditModal(false);
      // Rafraîchir les données
      const [clientsData, countData] = await Promise.all([
        getClients(token),
        getClientsCount(token)
      ]);
      
      // Ensure clientsData is an array
      if (Array.isArray(clientsData)) {
        setClients(clientsData);
      } else {
        setClients([]);
      }
      
      // Ensure countData has the expected structure
      if (countData && typeof countData.count === 'number') {
        setClientsCount(countData.count);
      } else {
        setClientsCount(0);
      }
      
      alert("Client modifié avec succès !");
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alert("Erreur lors de la modification du client.");
    }
  };

  // Supprimer un client
  const handleDeleteClient = (client) => {
    setCurrentClient(client);
    setShowDeleteModal(true);
    setShowMenu(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteClient(token, currentClient.id);
      if (response.message) {
        setShowDeleteModal(false);
        // Rafraîchir les données
        const [clientsData, countData] = await Promise.all([
          getClients(token),
          getClientsCount(token)
        ]);
        
        // Ensure clientsData is an array
        if (Array.isArray(clientsData)) {
          setClients(clientsData);
        } else {
          setClients([]);
        }
        
        // Ensure countData has the expected structure
        if (countData && typeof countData.count === 'number') {
          setClientsCount(countData.count);
        } else {
          setClientsCount(0);
        }
        
        alert("Client supprimé avec succès !");
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression du client.");
    }
  };

  // Supprimer plusieurs clients
  const handleDeleteSelected = async () => {
    if (selectedClients.length === 0) {
      alert("Aucun client sélectionné.");
      return;
    }

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedClients.length} client(s) ?`)) {
      try {
        for (const clientId of selectedClients) {
          await deleteClient(token, clientId);
        }
        setSelectedClients([]);
        // Rafraîchir les données
        const [clientsData, countData] = await Promise.all([
          getClients(token),
          getClientsCount(token)
        ]);
        
        // Ensure clientsData is an array
        if (Array.isArray(clientsData)) {
          setClients(clientsData);
        } else {
          setClients([]);
        }
        
        // Ensure countData has the expected structure
        if (countData && typeof countData.count === 'number') {
          setClientsCount(countData.count);
        } else {
          setClientsCount(0);
        }
        
        alert(`${selectedClients.length} client(s) supprimé(s) avec succès !`);
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        alert("Erreur lors de la suppression des clients.");
      }
    }
  };

  return (
    <div className="list-clients-wrapper">
      <div className="list-clients-card">
        <h2 style={{display: "flex" ,alignitems: "center ", gap: "10px", justifyContent: "start"}} className="list-clients-title">
          Liste des clients 
          <span class="stat-item"> <strong> {clientsCount} </strong> client{clientsCount > 1 ? 's' : ''}</span>
        </h2>

        <div className="table-container">
          <div className="table-header">
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <table className="clients-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>id</th>
                <th>Prénom(s)</th>
                <th>Nom</th>
                <th>Adresse</th>
                <th>Ville</th>
                <th>Téléphone</th>
                <th>Email</th>
                <th>Réf.</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-state">
                    {searchTerm ? "Aucun client trouvé pour cette recherche." : "Aucun client trouvé."}
                  </td>
                </tr>
              ) : (
                filteredClients.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => handleSelectClient(client.id, e.target.checked)}
                      />
                    </td>
                    <td>{client.id}</td>
                    <td>{client.contact}</td>
                    <td>{client.nom_entreprise}</td>
                    <td>{client.adresse}</td>
                    <td>{client.ville}</td>
                    <td>{client.telephone}</td>
                    <td>{client.email}</td>
                    <td>{client.reference_client}</td>
                    <td className="actions">
                      <button
                        className="menu-btn"
                        onClick={(e) => handleMenuClick(client.id, e)}
                      >
                        ⋯
                      </button>
                      {showMenu === client.id && (
                        <div className="menu">
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleViewClient(client)}>
                          <Eye size={15} color="#1a1a1a" weight="duotone" /> Voir client
                          </button>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleEditClient(client)}>
                          <Pencil size={15} color="#1a1a1a" weight="duotone" /> Modifier client
                          </button>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleDeleteClient(client)}>
                          <UserMinus size={15} color="#1a1a1a" weight="duotone" /> Supprimer client
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {selectedClients.length > 0 && (
            <button  style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start" }} className="delete-selected" onClick={handleDeleteSelected}>
              <UserMinus size={15} color="#f2f2f2" weight="duotone" /> Supprimer {selectedClients.length} client(s) sélectionné(s)
            </button>
          )}
        </div>
      </div>

      {/* Modal Voir Client */}
      {showViewModal && currentClient && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Détails du Client</h3>
              <button onClick={() => setShowViewModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="view-field">
                <strong>Prénom :</strong> {currentClient.contact}
              </div>
              <div className="view-field">
                <strong>Nom :</strong> {currentClient.nom_entreprise}
              </div>
              <div className="view-field">
                <strong>Adresse :</strong> {currentClient.adresse}
              </div>
              <div className="view-field">
                <strong>Code postal :</strong> {currentClient.code_postal}
              </div>
              <div className="view-field">
                <strong>Ville :</strong> {currentClient.ville}
              </div>
              <div className="view-field">
                <strong>Email :</strong> {currentClient.email}
              </div>
              <div className="view-field">
                <strong>Téléphone :</strong> {currentClient.telephone}
              </div>
              <div className="view-field">
                <strong>Référence :</strong> {currentClient.reference_client}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowViewModal(false)} className="btn-cancel">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier Client */}
      {showEditModal && currentClient && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Modifier Client</h3>
              <button onClick={() => setShowEditModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Prénom"
                value={editForm.contact || ""}
                onChange={(e) => setEditForm({...editForm, contact: e.target.value})}
              />
              <input
                type="text"
                placeholder="Nom"
                value={editForm.nom_entreprise || ""}
                onChange={(e) => setEditForm({...editForm, nom_entreprise: e.target.value})}
              />
              <input
                type="text"
                placeholder="Adresse"
                value={editForm.adresse || ""}
                onChange={(e) => setEditForm({...editForm, adresse: e.target.value})}
              />
              <input
                type="text"
                placeholder="Code postal"
                value={editForm.code_postal || ""}
                onChange={(e) => setEditForm({...editForm, code_postal: e.target.value})}
              />
              <input
                type="text"
                placeholder="Ville"
                value={editForm.ville || ""}
                onChange={(e) => setEditForm({...editForm, ville: e.target.value})}
              />
              <input
                type="email"
                placeholder="Email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
              />
              <input
                type="tel"
                placeholder="Téléphone"
                value={editForm.telephone || ""}
                onChange={(e) => setEditForm({...editForm, telephone: e.target.value})}
              />
              <input
                type="text"
                placeholder="Référence client"
                value={editForm.reference_client || ""}
                onChange={(e) => setEditForm({...editForm, reference_client: e.target.value})}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowEditModal(false)} className="btn-cancel">
                Annuler
              </button>
              <button onClick={handleSaveEdit} className="btn-save">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Supprimer Client */}
      {showDeleteModal && currentClient && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Supprimer un client</h3>
              <button onClick={() => setShowDeleteModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer le client :</p>
              <p><strong>{currentClient.contact} {currentClient.nom_entreprise}</strong> ?</p>
              <p>Cette action est irréversible.</p>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteModal(false)} className="btn-cancel">
                Annuler
              </button>
              <button onClick={handleDeleteConfirm} className="btn-delete-confirm">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ListClients;